import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast("Your password has been reset.", {
        description: "You can sign in with your new password now.",
      });
      navigate("/auth", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif-display font-light text-2xl sm:text-3xl tracking-tight">
          This link is incomplete.
        </h1>
        <p className="mt-4 text-[var(--mirror-text-3)] text-sm">
          Please open the reset link from your email again.
        </p>
        <Link
          to="/forgot-password"
          className="mt-8 text-sm tracking-[0.15em] uppercase text-[var(--mirror-accent)] hover:opacity-80"
        >
          Request a new link
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        onSubmit={submit}
        className="w-full max-w-sm"
      >
        <h1 className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight">
          Choose a new password.
        </h1>
        <p className="mt-5 text-xs text-[var(--mirror-text-3)] leading-relaxed">
          Use at least 10 characters, with an uppercase and lowercase letter, a number, and a
          special character.
        </p>

        <div className="mt-10">
          <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
            New password
          </label>
          <input
            data-testid="reset-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
          />
        </div>

        <div className="mt-6">
          <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
            Confirm password
          </label>
          <input
            data-testid="reset-confirm-input"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
          />
        </div>

        {error && (
          <p data-testid="reset-error" className="mt-4 text-sm text-[#d98a8a]">
            {error}
          </p>
        )}

        <button
          data-testid="reset-submit-button"
          type="submit"
          disabled={loading}
          className="mt-10 w-full py-3 rounded-full bg-[var(--mirror-accent)] text-[#16151F] text-sm tracking-[0.2em] uppercase font-medium hover:opacity-90 transition-opacity duration-500 disabled:opacity-50"
        >
          {loading ? "Resetting…" : "Reset password"}
        </button>
      </motion.form>
    </div>
  );
}
